import axios from 'axios';
import {
  BelongsCreatedByRefField,
  FieldTypes,
  pageCollectionFields,
  childDataField,
} from 'drapcode-constant';
import { dynamicSort } from '../../../common/utils/api_utils';
import { builderSurfaceJsonContentHeader } from '../../../common/utils/restApiHeaderUtils';
import { moveDefaultCollectionFiledsToEndAndSort } from '../../../common/utils/api_utils';
import { formDefaultStyle } from './form/consts';
import { getProjectById } from '../apiUtils';
import { formatCustomCSSClasses } from '../../../common/utils/jsonUtils';

export const getCollectionFields = async (collectionName, collections) => {
  if (!collections) {
    return null;
  }
  return collections.find((collection) => collection.collectionName === collectionName);
};
export const getSnippets = async (projectId, version) => {
  const url = `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/projects/${projectId}/snippets`;
  const header = builderSurfaceJsonContentHeader(projectId);
  return axios.get(url, header);
};
export const getCustomSegmentSnippets = async (projectId, version) => {
  const url = `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/projects/${projectId}/placeholders`;
  const header = builderSurfaceJsonContentHeader(projectId);
  return axios.get(url, header);
};
export const getCustomComponents = async (projectId, version) => {
  const url = `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/custom-component`;
  const header = builderSurfaceJsonContentHeader(projectId);
  return axios.get(url, header);
};
export const getSnippetContent = async (projectId, version, snippetUuid) => {
  if (!snippetUuid) {
    return {};
  }
  const url = `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/projects/${projectId}/snippet-templates/${snippetUuid}/content`;
  const header = builderSurfaceJsonContentHeader(projectId);
  return axios.get(url, header);
};

export const getCollectionFieldTraitOptions = async (collection, collections) => {
  if (!collection) {
    return [];
  }
  const { fields, utilities, projectId } = collection;
  const utilityOptions = getUtilitiesOptions(utilities);
  const nestedFields = await getInnerFieldOptions(fields, projectId, collections);
  const fieldOptions = fields
    ? moveDefaultCollectionFiledsToEndAndSort(fields).map((item, key) => {
        if (childDataField.includes(item.type)) {
          const { collectionName, collectionField } = item.refCollection;
          const fieldData = {
            nestedFieldName: collectionField,
            collectionName,
            fieldType: 'text',
            fieldName: item.fieldName,
          };
          return {
            id: item.fieldName,
            itemId: item._id,
            metaData: JSON.stringify(fieldData),
            name: item.fieldTitle.en,
            type: item.type,
          };
        } else {
          return { id: item.fieldName, name: item.fieldTitle.en, type: item.type };
        }
      })
    : [];
  let options = [...fieldOptions, { id: '', name: '-------' }, ...utilityOptions, ...nestedFields];
  return options;
};

export const getInnerFieldOptions = async (fields, projectId, collections) => {
  const referenceFields = fields
    ? fields.filter((field) => BelongsCreatedByRefField.includes(field.type))
    : [];
  if (!referenceFields || referenceFields.length === 0) {
    return [];
  }
  let nestedFields = [];
  await Promise.all(
    referenceFields.map(async (field) => {
      if (
        [FieldTypes.createdBy.id, FieldTypes.updatedBy.id].includes(field.type) &&
        !field.refCollection
      ) {
        field.refCollection = {
          collectionName: 'user',
          collectionField: 'userName',
          displayType: 'dropdown',
        };
      }
      let { collectionName } = field.refCollection;
      let refFields = [];
      let utilityOptions = [];
      if (collectionName) {
        try {
          const collectionRes = await getCollectionFields(collectionName, collections);
          if (collectionRes) {
            utilityOptions = getUtilitiesOptionsForNestedFields(collectionRes.utilities, field);
            refFields = collectionRes.fields.map((nestedField) => {
              const fieldData = {
                nestedFieldName: nestedField.fieldName,
                collectionName,
                fieldType: nestedField.type,
                fieldName: field.fieldName,
              };
              return {
                id: field.fieldName + '.' + nestedField.fieldName,
                type: field.type,
                metaData: JSON.stringify(fieldData),
                name: field.fieldTitle.en + ':' + nestedField.fieldTitle.en,
              };
            });
          }
        } catch (e) {
          console.log('error In refrence field collection api by name', e);
        }
      }

      nestedFields = [...nestedFields, ...refFields, ...utilityOptions];
    }),
  );
  return nestedFields;
};

export const getUtilitiesOptions = (utilities) => {
  const SPACE_KEYWORD = '#SPACE#';
  return utilities
    ? utilities.map((utility) => {
        const utilityJson = { args: utility.args, functionType: utility.functionType };
        let utilityJsonString = JSON.stringify(utilityJson).split('"').join("'");
        if (utility.functionType === 'FORMAT_DATE') {
          utilityJsonString = JSON.stringify(utilityJson)
            .split('"')
            .join("'")
            .replace(/ /g, SPACE_KEYWORD); //Handling Spaces in Date Format
        }

        return {
          id: utilityJsonString,
          type: 'utility',
          name: utility.name,
        };
      })
    : [];
};

export const getUtilitiesOptionsForNestedFields = (utilities, field) => {
  return utilities
    ? utilities.map((utility) => {
        const utilityJson = {
          args: utility.args,
          functionType: utility.functionType,
          parentFieldName: field.fieldName,
        };
        return {
          id: JSON.stringify(utilityJson).split('"').join("'"),
          type: 'utility',
          name: field.fieldTitle.en + ':' + utility.name,
        };
      })
    : [];
};

export const getFieldOptionsForSelect = (options, selectNone = '') => {
  let optionHtml = `<option value="" ${selectNone === '' ? 'selected' : ''}>None</option>`;
  optionHtml += getOptionHtml(options);
  return optionHtml;
};

export const getFieldOptionsForProgressSelect = (options, selectNone = '') => {
  let optionHtml = `<option value="" ${selectNone === '' ? 'selected' : ''}>None</option>`;
  optionHtml += getOptionHtml(options, ['number', 'text']);
  return optionHtml;
};

const getOptionHtml = (options, allowedTypes = []) => {
  const optionHtml = [];
  options.forEach((opt) => {
    if (allowedTypes.length && !allowedTypes.includes(opt.type)) return;
    optionHtml.push(
      `<option value="${opt.id}"  metaData= "${
        opt.metaData ? opt.metaData.split('"').join("'") : ''
      }" type="${opt.type}">${opt.name}</option>`,
    );
  });

  return optionHtml.join('');
};

export const getFieldOptionsForAddToRemoveFromSelect = (options, selectNone = '') => {
  let optionHtml = `<option value="" ${selectNone === '' ? 'selected' : ''}>None</option>`;
  optionHtml += options
    .map(
      (opt) =>
        `<option value="${opt.itemId}#${opt.id}#${
          opt.metaData ? opt.metaData.split('"').join("'").collectionName : ''
        }"  metaData= "${opt.metaData ? opt.metaData.split('"').join("'") : ''}" type="${
          opt.type
        }">${opt.name}</option>`,
    )
    .join('');
  return optionHtml;
};

export const getFieldOptionsForCollectionSelect = (
  options,
  pageContextCollection = '',
  label = 'Select Field',
  selectNone = '',
) => {
  let optionHtml = `<option value="" ${selectNone === '' ? 'selected' : ''}>${label}</option>`;
  optionHtml += options
    ? options
        .filter((opt) => opt.type === 'reference' || opt.isMultiSelect)
        .map(
          (opt) => `<option value="${opt._id}#${opt.fieldName}#${
            pageContextCollection ? pageContextCollection : opt.refCollection.collectionName
          }">
        ${opt.fieldTitle.en}</option>`,
        )
        .join('')
    : [];

  return optionHtml;
};

export const getCollectionOptionsForSelect = (collections) => {
  let optionHtml = `<option value="" selected>- Select Collection -</option>`;
  if (collections) {
    optionHtml += collections
      .sort(dynamicSort('collectionTitle'))
      .map(
        (collection) =>
          `<option value="${collection.collectionName}">${collection.collectionTitle}</option>`,
      )
      .join('');
  }

  return optionHtml;
};

export const getTypesenseCollectionOptionsForSelect = (collections) => {
  let optionHtml = `<option value="" selected>- Select Typesense Collection -</option>`;
  if (collections) {
    collections = collections.filter((col) => col.typesenseMapping && col.typesenseMapping.length);
    optionHtml += collections
      .sort(dynamicSort('collectionTitle'))
      .map(
        (collection) =>
          `<option value="${collection.collectionName}">${collection.collectionTitle}</option>`,
      )
      .join('');
  }

  return optionHtml;
};

export const getLocalizationOptionsForSelect = async (projectId) => {
  const { data: localizations } = await getLocalizationConfigs(projectId);
  const { languageKeyValues } =
    localizations && localizations.length ? localizations.find((local) => local.isDefault) : '';
  let optionHtml = `<option value="" selected>- Select Localization Key -</option>`;
  if (languageKeyValues) {
    optionHtml += languageKeyValues
      .sort(dynamicSort('key'))
      .map(({ key }) => `<option value="${key}">${key}</option>`)
      .join('');
  }
  return optionHtml;
};
export const getTextCollectionFinderOptions = (selectedCollection) => {
  let finders = selectedCollection ? selectedCollection.finders : [];
  finders = finders.filter((item) => ['COUNT', 'SUM', 'AVG', 'MIN', 'MAX'].includes(item.finder));
  let optionHtml = `<option value="" selected>- Select Filter -</option>`;
  optionHtml += finders
    ? finders.map((filter) => `<option value="${filter.uuid}">${filter.name}</option>`).join('')
    : [];
  return optionHtml;
};

export const getSingleCollectionFinderOptions = (selectedCollection) => {
  let finders = selectedCollection ? selectedCollection.finders : [];
  finders = finders.filter((item) =>
    ['COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'FIND'].includes(item.finder),
  );
  let optionHtml = `<option value="" selected>- Select Filter -</option>`;
  optionHtml += finders
    ? finders.map((filter) => `<option value="${filter.uuid}">${filter.name}</option>`).join('')
    : [];
  return optionHtml;
};
export const getAllCollectionFinderOptions = (selectedCollection) => {
  let finders = selectedCollection ? selectedCollection.finders : [];
  finders = finders.filter((item) => ['COUNT', 'SUM', 'AVG', 'MIN', 'MAX'].includes(item.finder));
  let optionHtml = `<option value="" selected>- Select Filter -</option>`;
  optionHtml += finders
    ? finders.map((filter) => `<option value="${filter.uuid}">${filter.name}</option>`).join('')
    : [];
  return optionHtml;
};

const getExternalParamsOptions = (externalQueryParamKeys, collections, pageCollectionId) => {
  const pageCollection = collections.find(
    (collection) => collection.collectionName === pageCollectionId,
  );
  const pageFields = pageCollection
    ? pageCollection.fields.filter((field) => pageCollectionFields.includes(field.type))
    : [];
  return externalQueryParamKeys
    .map((params) => {
      const key = Object.keys(params);
      const value = params[key];
      let optionHtml = `<option value="" ${value ? '' : 'selected'}>- Select Field -</option>`;
      return `<div class="gjs-row" >
                <div class="external-params-div gjs-label-wrp">
                  <div class="gjs-label" title="${key}">${key}</div>
                </div>
                <div class="select-params-div gjs-field-wrp gjs-field-wrp--select-filter-div">
                  <div class="gjs-field">
                    <select value="3" params="${key}" class="select-external-param form-control form-control-sm options-light">
                      ${(optionHtml +=
                        pageFields && pageFields.length > 0
                          ? pageFields
                              .map(
                                (field) =>
                                  `<option ${value === field.fieldName ? 'selected' : ''} value="${
                                    field.fieldName
                                  }">${field.fieldTitle.en}</option>`,
                              )
                              .join('')
                          : '')}
                    </select>
                  </div>
                </div>
              </div>`;
    })
    .join('');
};

export const getFinderFieldsOptions = (
  collections,
  selectedCollectionName,
  finderId,
  pageCollectionId,
  externalQueryParamKeys = [],
) => {
  const selectedCollection = collections.find(
    (collection) => collection.collectionName === selectedCollectionName,
  );
  let finders = selectedCollection ? selectedCollection.finders : [];
  const selectedFinder = finders.find((item) => item.uuid === finderId);
  if (selectedFinder && selectedFinder.conditions) {
    if (externalQueryParamKeys.length === 0) {
      selectedFinder.conditions.forEach((condition) => {
        if (condition.requiredExternal) {
          const {
            query: { value },
          } = condition;
          externalQueryParamKeys.push({ [value]: '' });
        }
      });
    }
  }
  return getExternalParamsOptions(externalQueryParamKeys, collections, pageCollectionId);
};

export const getLocalizationConfigs = async (projectId) => {
  let url = `${process.env.REACT_APP_BUILDER_ENGINE}localization`;
  const header = builderSurfaceJsonContentHeader(projectId);
  return axios.get(url, header);
};

export const getPreBuiltSections = async (projectId, category = 'ALL') => {
  let url = `${process.env.REACT_APP_BUILDER_ENGINE}pre-built-section/`;
  if (category && category !== 'ALL') {
    url = `${url}?category=${category}`;
  }
  const header = builderSurfaceJsonContentHeader(projectId);
  return axios.get(url, header);
};

export const getCustomFormThemesStyle = async () => {
  return formDefaultStyle;
};

export const getCustomCSSClasses = async (projectId, version) => {
  const projectResponse = await getProjectById(projectId, version);
  const customCSSClasses =
    projectResponse && projectResponse.status === 200 && projectResponse.data.customCSSClasses
      ? projectResponse.data.customCSSClasses
      : [];
  return customCSSClasses;
};

export const loadCustomCSSClasses = async (projectId, version) => {
  const projectResponse = await getProjectById(projectId, version);
  const customCSSClasses =
    projectResponse && projectResponse.status === 200 && projectResponse.data.customCSSClasses
      ? projectResponse.data.customCSSClasses
      : [];

  let projectCustomCSSClassesData = formatCustomCSSClasses(customCSSClasses);
  return projectCustomCSSClassesData;
};
export const loadCollectionItems = async (projectId, version, collectionName) => {
  const url = `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/items/${collectionName}`;
  const header = builderSurfaceJsonContentHeader(projectId);
  const response = await axios.get(url, header);
  return response.status === 200 ? response.data : [];
};

export const getExternalAPIOptionsForSelect = (
  externalAPIs,
  sourceOfOrigin = '',
  collectionName = '',
) => {
  let optionHtml = `<option value="" selected>- Select External API -</option>`;
  if (collectionName && externalAPIs) {
    externalAPIs = externalAPIs.filter((externalAPI) => {
      const { responseDataMapping } = externalAPI;
      const { selectedCollectionName } = responseDataMapping || {};
      return selectedCollectionName === collectionName;
    });
  }
  if (sourceOfOrigin && externalAPIs) {
    if (sourceOfOrigin === 'DEFAULT') {
      sourceOfOrigin = 'REST_API';
    }
    externalAPIs = externalAPIs.filter((externalAPI) => {
      const { externalApiType, responseDataMapping } = externalAPI;
      const { selectedCollectionName } = responseDataMapping || {};
      return externalApiType === sourceOfOrigin && selectedCollectionName;
    });
  }
  if (externalAPIs) {
    optionHtml += externalAPIs
      .sort(dynamicSort('externalApiName'))
      .map((external) => `<option value="${external.uuid}">${external.externalApiName}</option>`)
      .join('');
  }
  return optionHtml;
};
export const loadThemePageContent = async (url) => {
  const { data } = await axios.get(url);
  return data;
};
export const getInstalledPlugins = async (projectId, version) => {
  const url = `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/plugins/`;
  const header = builderSurfaceJsonContentHeader(projectId);
  const { data } = await axios.get(url, header);
  return data;
};

export const fetchBuilderAiData = async (endpoint, formData, version, isJsonData = false) => {
  const serverUrl = `${process.env.REACT_APP_BUILDER_AI_ENGINE}v${version || 1}`;
  const url = `${serverUrl}${endpoint}`;
  const headers = { Accept: 'application/json' };
  if (isJsonData) {
    headers['Content-Type'] = 'application/json';
  }

  return await fetch(url, {
    method: 'POST',
    maxBodyLength: 'Infinity',
    headers: headers,
    credentials: 'include', // Include cookies if needed
    body: formData,
  });
};

export const addComponentCssJsInProject = async (projectId, version, extracted) => {
  const serverUrl = `${process.env.REACT_APP_BUILDER_ENGINE}v${version || 1}`;
  const baseURL = `${serverUrl}/projects/${projectId}/`;
  const url = `${baseURL}update-ux-content`;
  const header = {
    headers: {
      'Content-Type': 'application/json',
      'x-project-id': projectId,
    },
  };
  return axios
    .put(url, extracted, header)
    .then((response) => {
      console.log('🚀 ~ apiCallUpdateCssJsContent ~ response:', response);
    })
    .catch((err) => {
      console.error('🚀 ~ apiCallUpdateCssJsContent ~ err:', err);
    });
};
